import {GameObject} from "../../tsthree";
import { Container, Graphics, NineSlicePlane, Rectangle, Text, TextStyle, Texture } from "pixi.js";
import {buttonify} from "../../engine/HelperFunctions/buttonify";
import {GAME_FONT} from "../Constants/Constants";
import MiniSearch, {SearchResult} from "minisearch";
import {SpecialPageTypes} from "../Data/WebsiteContents";
import {HospitalIDMapper} from "../Data/HospitalIDMapper";
import isMobile from "is-mobile";
import {isTouchDevice} from "../../engine/HelperFunctions/isTouchDevice";
import { DoctOSWindowTypeToSpecialPageType } from "./Windows/Win95DoctOSWindow";
import { Plane } from "three";
import { PlayerDataSingleton } from "../Logic/PlayerDataSingleton";
import { GAME_DEBUG_MODE } from "../../engine/Constants/Constants";

const mobileDeviceInputGrabberSingleton = document.createElement("input");

export class SearchbarPrefab extends GameObject {
    private static _minisearchCaches: Record<string, MiniSearch> = {};
    private static _currentSelectedInstance: SearchbarPrefab;

    private _acceptingInput: boolean = false;

    private _textOffset: number = 0;
    public get textOffset(): number {
        return this._textOffset;
    }
    public set textOffset(_val: number) {
        this._textOffset = _val;
        this._searchBarText.position.set(4 + _val, 4);
    }

    private _onSearch: (_results: { name: string, id: string, type?: string }[]) => void;
    private _onFeelingLucky: () => void;
    private _isInitialized: boolean = false;

    private _searchBarBg: NineSlicePlane;
    private _searchBarText: Text;
    private _carat: Text;
    private _caratTick: number = 0;

    private _allowFreeInput: boolean = false;
    private _fuzzy: number = 0.1;
    private _prefix: boolean = true;
    private _minisearchKey: string = "default";

    private _skipPreview: boolean = false;

    private _searchFilterFunction: (params: any) => boolean;

    private searchPreviewElements: {
        container: Container;
        textPool: Text[];
        bg: NineSlicePlane;
        selectedElementBg: Graphics;
    };

    public get currentText(): string {
        return this._searchBarText.text;
    }

    public set currentText(val: string) {
        this._searchBarText.text = val;
        this._carat.position.set(
            0 + (this._searchBarText.text == "" ? 0 : this._searchBarText.width) + this._textOffset,
            this._carat.position.y,
        );
        this.searchPreviewElements.container.visible = false;
        this.searchPreviewElements.selectedElementBg.visible = false;
    }

    public static getMinisearchCaches(): Record<string, MiniSearch> {
        return this._minisearchCaches;
    }

    public static setMinisearchCache(key: string, obj: MiniSearch): void {
        this._minisearchCaches[key] = obj;
    }

    public static getMinisearchCache(_key: string = "default"): MiniSearch {
        return this._minisearchCaches[_key];
    }

    public static hasMinisearchCache(_key: string): boolean {
        return Boolean(this._minisearchCaches[_key]);
    }

    public get acceptingInput(): boolean {
        return this._acceptingInput;
    }

    public set acceptingInput(_val: boolean) {
        if(SearchbarPrefab._currentSelectedInstance && SearchbarPrefab._currentSelectedInstance !== this)
            SearchbarPrefab._currentSelectedInstance.acceptingInput = false;
        SearchbarPrefab._currentSelectedInstance = this;
        this._acceptingInput = _val;
        this._carat.visible = _val;
    }

    constructor(_settings: {
        width: number;
        height?: number;
        fuzzy?: number;
        prefix?: boolean;
        allowFreeInput?: boolean;
        minisearchKey?: string;
        skipButtons: boolean;
        onType: () => void;
        searchFilterFunction?: (params: any) => boolean;
        skipPreview?: boolean;
    }) {
        super("SearchbarPrefab");

        this._fuzzy = _settings.fuzzy || this._fuzzy;
        this._allowFreeInput = _settings.allowFreeInput || this._allowFreeInput;
        this._prefix = _settings.prefix || this._prefix;
        this._minisearchKey = _settings.minisearchKey || this._minisearchKey;
        this._searchFilterFunction = _settings.searchFilterFunction || null;
        this._skipPreview = _settings.skipPreview;

        const searchBarBg = this._searchBarBg = new NineSlicePlane(
            ENGINE.getPIXIResource("inner-container-nineslice") as Texture
        );
        searchBarBg.height = _settings.height || 20;
        searchBarBg.width = _settings.width;
        searchBarBg.position.set(-(searchBarBg.width * 0.5), 0);
        this.addChild(searchBarBg);

        buttonify(searchBarBg, {
            disableButtonMode: true,
            onFire: () => this.setAcceptInput(),
            onPointerOver: () => {
                document.body.style.cursor = "text";
            },
            onPointerOut: () => {
                document.body.style.cursor = "auto";
            }
        });

        const carat = this._carat = new Text("|", new TextStyle({
            fill: 0x000000,
            fontSize: 14,
            fontFamily: GAME_FONT,
        }));
        carat.position.set(4, 2);
        carat.visible = false;
        searchBarBg.addChild(carat);

        const searchBarText = this._searchBarText = new Text("", new TextStyle({
            fill: 0x000000,
            fontSize: 11,
            fontFamily: GAME_FONT,
        }));
        searchBarText.position.set(4, 4);
        searchBarBg.addChild(searchBarText);

        if(!_settings.skipButtons) {
            const drugleSearchBg = new NineSlicePlane(ENGINE.getPIXIResource("button") as Texture);
            drugleSearchBg.width = 100;
            drugleSearchBg.height = 16;
            drugleSearchBg.position.set(-102, 19);
            const drugleSearchText = new Text("Drugle Search", new TextStyle({
                fill: 0x000000,
                fontSize: 11,
                fontFamily: GAME_FONT,
                align: "center"
            }));
            drugleSearchText.anchor.set(0.5);
            drugleSearchText.position.set(drugleSearchBg.width * 0.5, drugleSearchBg.height * 0.5);
            drugleSearchBg.addChild(drugleSearchText);
            this.addChild(drugleSearchBg);
            buttonify(drugleSearchBg, {
                onFire: () => {
                    const results = SearchbarPrefab._minisearchCaches[
                            this._minisearchKey
                    ].search(
                        this._searchBarText.text,
                        {
                            prefix: this._prefix,
                            fuzzy: this._fuzzy,
                            filter: this._searchFilterFunction
                        }
                    );
                    this._onSearch(results.map((e) => e.name));
                }
            });

            const feelingLuckyBg = new NineSlicePlane(ENGINE.getPIXIResource("button") as Texture);
            feelingLuckyBg.width = 100;
            feelingLuckyBg.height = 16;
            feelingLuckyBg.position.set(2, 19);
            const feelingLuckyBgText = new Text("Feeling Fortunate", new TextStyle({
                fill: 0x000000,
                fontSize: 11,
                fontFamily: GAME_FONT,
                align: "center"
            }));
            feelingLuckyBgText.anchor.set(0.5);
            feelingLuckyBgText.position.set(drugleSearchBg.width * 0.5, drugleSearchBg.height * 0.5);
            feelingLuckyBg.addChild(feelingLuckyBgText);
            this.addChild(feelingLuckyBg);
            buttonify(feelingLuckyBg, {
                onFire: () => {
                    // const results = SearchbarPrefab._minisearchCache.search(
                    //     this._searchBarText.text,
                    //     {
                    //         prefix: true,
                    //         fuzzy: 0.2,
                    //     }
                    // );
                    this._onFeelingLucky();
                }
            });
        }

        const POOL_SIZE = 5;
        const searchPreviewContainer = new Container();
        searchPreviewContainer.position.set(searchBarBg.position.x, searchBarBg.position.y + searchBarBg.height);
        const searchPreviewBg = new NineSlicePlane(
            ENGINE.getPIXIResource("white-square-stroke") as Texture,
            2, 2, 2, 2
        );
        searchPreviewBg.width = searchBarBg.width;
        searchPreviewBg.height = 20 * POOL_SIZE;
        searchPreviewContainer.addChild(searchPreviewBg);
        const searchPreviewTextPool: Text[] = [];
        const slctPreviewBg = new Graphics();
        slctPreviewBg.beginFill(0x0000ff);
        slctPreviewBg.drawRect(0, 0, searchPreviewBg.width, 20);
        slctPreviewBg.endFill();
        searchPreviewContainer.addChild(slctPreviewBg);
        for(let i = 0; i < POOL_SIZE; i++) {
            const text = new Text(
                "",
                new TextStyle({
                    fill: i === 0 ? 0xffffff : 0x000000,
                    fontSize: 11,
                    fontFamily: GAME_FONT,
                })
            );
            text.hitArea = new Rectangle(0, 0, searchPreviewBg.width, 20);
            text.position.set(4, (20 * i) + 4);
            searchPreviewContainer.addChild(text);
            searchPreviewTextPool.push(text);
        }
        searchPreviewContainer.visible = false;
        this.addChild(searchPreviewContainer);
        this.searchPreviewElements = {
            bg: searchPreviewBg,
            container: searchPreviewContainer,
            textPool: searchPreviewTextPool,
            selectedElementBg: slctPreviewBg,
        };

        this.addInput(_settings.onType);
    }

    public setAcceptInput(): void {
        this.acceptingInput = true;
        this._carat.visible = true;
        SearchbarPrefab._currentSelectedInstance = this;
        if(isMobile() || isTouchDevice()) {
            mobileDeviceInputGrabberSingleton
                .setAttribute(
                    "style",
                    "position:absolute; right: 0px; width: 100%; height: 5%; top: 0px; opacity: 0;"
                );
            mobileDeviceInputGrabberSingleton
                .setAttribute("type", "text");
            document.body.appendChild(mobileDeviceInputGrabberSingleton);
            mobileDeviceInputGrabberSingleton.oninput = () => {
                this._searchBarText.text = mobileDeviceInputGrabberSingleton.value;
                this._carat.position.set(
                    0 + (this._searchBarText.text == "" ? 0 : this._searchBarText.width) + this._textOffset,
                    this._carat.position.y,
                );
                this._caratTick = 0;
                this._carat.visible = true;
            };
            mobileDeviceInputGrabberSingleton.focus();
        }
    }

    private addInput(
        onType: () => void
    ) {
        if(this._isInitialized) return;
        const onSearch = (_retVal?: string) => {
            if(!this._onSearch) return;
            if(_retVal) {
                if(GAME_DEBUG_MODE) console.log(_retVal);
                this._onSearch([
                    {
                        name: _retVal,
                        id: _retVal,
                    }
                ]);
            }
            this.searchPreviewElements.container.visible = false;
            document.body.style.cursor = "auto";

            if(this._allowFreeInput && this.searchPreviewElements.selectedElementBg.visible == false) {
                if(this.searchPreviewElements?.selectedElementBg.visible) {
                    const ind = Math.floor((this.searchPreviewElements.selectedElementBg.position.y) / 20);
                    // console.log(this.searchPreviewElements.textPool[ind].text);
                    this._onSearch([{
                        name: this.searchPreviewElements.textPool[ind].text,
                        // @ts-ignore
                        id: this.searchPreviewElements.textPool[ind]["__IDHACK"],
                        // @ts-ignore
                        type: this.searchPreviewElements.textPool[ind]["__TYPEHACK"]
                    }]);
                } else {
                    this._onSearch([{
                        name: this._searchBarText.text,
                        id: this._searchBarText.text,
                        // @ts-ignore
                        // type: this.searchPreviewElements.textPool[ind]["__TYPEHACK"]
                    }]);
                }
            } else if(this._skipPreview) {
                const results = SearchbarPrefab._minisearchCaches[
                    this._minisearchKey
                ].search(
                    this._searchBarText.text,
                    {
                        prefix: this._prefix,
                        fuzzy: this._fuzzy,
                        filter: this._searchFilterFunction
                    }
                );
                this._onSearch(results.map((e) => e.name));
            } else {
                const ind = Math.floor((this.searchPreviewElements.selectedElementBg.position.y) / 20);
                this._onSearch([{
                    name: this.searchPreviewElements.textPool[ind].text,
                    // @ts-ignore
                    id: this.searchPreviewElements.textPool[ind]["__IDHACK"],
                    // @ts-ignore
                    type: this.searchPreviewElements.textPool[ind]["__TYPEHACK"]
                }]);
            }
        };
        const inputHandler = (ev: KeyboardEvent) => {
            // debugger;
            if(!this.acceptingInput) {
                return;
            }
            if(
                ev.key !== "Backspace" &&
                ev.key !== "Enter" &&
                ev.key !== "ArrowDown" &&
                ev.key !== "ArrowUp" &&
                ev.key.length > 1
            ) return;

            const oldLen = this._searchBarText.text.length;
            let skipPreview: boolean = this._skipPreview || false;

            onType();

            if(ev.code === "ArrowDown" || ev.code === "ArrowUp") {
                const isUp: boolean = ev.code === "ArrowUp";

                const oldInd = Math.floor((this.searchPreviewElements.selectedElementBg.position.y) / 20);
                const newInd = Math.min(Math.max(
                    Math.floor(
                    (
                        this.searchPreviewElements.selectedElementBg.position.y + (isUp ? -20 : 20)
                    ) / 20),
                    0
                ), this.searchPreviewElements.textPool.filter((e) => e.text !== "" && e.visible).length - 1);
                if(
                    newInd === -1
                ) return;
                if(this.searchPreviewElements.selectedElementBg.visible == false) {
                    this.searchPreviewElements.selectedElementBg.visible = true;
                    this.searchPreviewElements.selectedElementBg.position.set(
                        0,
                        0,
                    );
                    this.searchPreviewElements.textPool[0].style.fill = 0xffffff;
                } else {
                    this.searchPreviewElements.selectedElementBg.position.set(
                        0,
                        newInd * 20,
                    );
                    this.searchPreviewElements.textPool[oldInd].style.fill = 0x000000;
                    this.searchPreviewElements.textPool[newInd].style.fill = 0xffffff;
                }
            } else if(ev.key === "Backspace") {
                this._searchBarText.text =
                    this._searchBarText.text.substring(0, this._searchBarText.text.length - 1);
                if(mobileDeviceInputGrabberSingleton) {
                    mobileDeviceInputGrabberSingleton.value = this._searchBarText.text;
                }
                this._carat.position.set(
                    0 + (this._searchBarText.text == "" ? 0 : this._searchBarText.width) + this._textOffset,
                    this._carat.position.y,
                );
                this._caratTick = 0;
                this._carat.visible = true;
            } else if(ev.key === "Enter" || ev.key === "Tab") {
                this.acceptingInput = false;
                skipPreview = true;
                ev.preventDefault();
                onSearch();
            } else {
                this._searchBarText.text += ev.key;
                this._carat.position.set(
                    0 + (this._searchBarText.text == "" ? 0 : this._searchBarText.width) + this._textOffset,
                    this._carat.position.y,
                );
                this._caratTick = 0;
                this._carat.visible = true;
            }

            const newLen = this._searchBarText.text.length;
            if(oldLen !== newLen && newLen !== 0 && skipPreview === false) {
                // const lgl = SearchbarPrefab.getMinisearchCache();
                const results = SearchbarPrefab._minisearchCaches[
                    this._minisearchKey
                ].search(
                    this._searchBarText.text,
                    {
                        fuzzy: 0.1,
                        prefix: true,
                        filter: this._searchFilterFunction || null,
                    }
                ).slice(0, 5);
                this.searchPreviewElements.textPool.forEach((e, i) => {
                    if(results[i]) {
                        e.text = results[i].name;
                        e.visible = true;

                        // HACK
                        // it's late, i'm tired, time for a hack.
                        // @ts-ignore
                        e["__IDHACK"] = results[i].id;
                        // @ts-ignore
                        e["__TYPEHACK"] = results[i].type;
                        // HACK
                    } else {
                        e.visible = false;
                    }
                });
                this.searchPreviewElements.selectedElementBg.visible = !this._allowFreeInput && results.length !== 0;
                this.searchPreviewElements.bg.height = 20 * results.length;
                this.searchPreviewElements.container.visible = true;
                this.searchPreviewElements.selectedElementBg.position.y = 0;
                this.searchPreviewElements.textPool.forEach((e, i) => {
                    if(i === 0 && this.searchPreviewElements.selectedElementBg.visible) {
                        e.style.fill = 0xffffff;
                    } else {
                        e.style.fill = 0x000000;
                    }
                });
            } else if(newLen === 0) {
                this.searchPreviewElements.container.visible = false;
            }
        };
        this.searchPreviewElements.textPool.forEach((text, i) => {
            buttonify(text, {
                disableButtonMode: false,
                onFire: () => {
                    if(GAME_DEBUG_MODE) {
                        console.log("Selecting autocomplete %s", text.text);
                    }
                    onSearch(text.text);
                },
                onPointerOver: () => {
                    const oldInd = Math.floor((this.searchPreviewElements.selectedElementBg.position.y) / 20);
                    this.searchPreviewElements.selectedElementBg.position.y = (20 * i);
                    const newInd = Math.floor((this.searchPreviewElements.selectedElementBg.position.y) / 20);
                    // this.searchPreviewElements.selectedElementBg.visible = true;
                    if(newInd !== -1 && newInd < this.searchPreviewElements.textPool.length) {

                    }
                    this.searchPreviewElements.textPool[oldInd].style.fill = 0x000000;
                    this.searchPreviewElements.textPool[newInd].style.fill = 0xffffff;
                    // if(oldInd == newInd) return;
                    this.searchPreviewElements.selectedElementBg.position.set(
                        0,
                        newInd * 20,
                    );
                    this.searchPreviewElements.selectedElementBg.visible = true;
                }
            });
        });
        window.addEventListener("keydown", inputHandler, false);

        this._isInitialized = true;
    }

    public updateSearch(
        _onSearch: (_results: { name: string, id: string, type?: string }[]) => void,
        _onFeelingLucky: () => void
    ): void {
        if(!SearchbarPrefab.hasMinisearchCache("default")) {
            SearchbarPrefab._minisearchCaches["default"] = new MiniSearch({
                fields: ['name'], // fields to index for full-text search
                storeFields: ['name', 'id', 'type'] // fields to return with search results
            });
            SearchbarPrefab._minisearchCaches["default"].addAll(
                SearchbarPrefab.buildMinisearchObjectForHospitalData(SpecialPageTypes.Symptoms)
            );
            SearchbarPrefab._minisearchCaches["default"].addAll(
                SearchbarPrefab.buildMinisearchObjectForHospitalData(SpecialPageTypes.Exams)
            );
            SearchbarPrefab._minisearchCaches["default"].addAll(
                SearchbarPrefab.buildMinisearchObjectForHospitalData(SpecialPageTypes.Diagnoses)
            );
            SearchbarPrefab._minisearchCaches["default"].addAll(
                SearchbarPrefab.buildMinisearchObjectForHospitalData(SpecialPageTypes.Treatments)
            );
        }
        if(!SearchbarPrefab.hasMinisearchCache("urls")) {
            SearchbarPrefab._minisearchCaches["urls"] = new MiniSearch({
                fields: ['name', 'id'], // fields to index for full-text search
                storeFields: ['name', 'id'] // fields to return with search results
            });
            SearchbarPrefab._minisearchCaches["urls"].removeAll();
            SearchbarPrefab._minisearchCaches["urls"].addAll(
                SearchbarPrefab.buildBasicMinisearchObject(
                    PlayerDataSingleton.getKnownUrls()
                )
            );
        }

        this._searchBarText.text = "";
        if(isMobile() || isTouchDevice()) {
            this._onSearch = (_results: { name: string, id: string }[]) => {
                mobileDeviceInputGrabberSingleton.remove();
                mobileDeviceInputGrabberSingleton.oninput = null;
                mobileDeviceInputGrabberSingleton.value = "";
                _onSearch(_results);
            };
        } else {
            this._onSearch = _onSearch;
        }
        this._onFeelingLucky = _onFeelingLucky;
    }

    public hide() {
        if(this.parent) {
            this.parent.removeChild(this);
        }
        this.visible = false;
    }

    public show(_parent: Container) {
        this.visible = true;
        _parent.addChild(this);
        this._carat.position.set(4 + this._textOffset, 2);
        this._searchBarText.text = "";
    }

    public static buildBasicMinisearchObject(_strings: string[]): any[] {
        return _strings.map((e) => ({
            id: e, //.replace(/http:\/\/|www\.|\.com|\.net/g, ""),
            name: e,
        }));
    }

    public static buildMinisearchObjectForHospitalData(_type: SpecialPageTypes): any[] {
        let strs: any[] = [];
        let keys: string[];
        switch(_type) {
            case SpecialPageTypes.Diagnoses:
                keys = Object.keys(HospitalIDMapper["mapMap"]["diagnosesToSymptoms"]["data"]);
                strs.push(...keys);
                break;
            case SpecialPageTypes.Symptoms:
                keys = Object.keys(HospitalIDMapper["mapMap"]["symptomsToDiagnoses"]["data"]);
                strs.push(...keys);
                break;
            case SpecialPageTypes.Exams:
                keys = Object.keys(HospitalIDMapper["mapMap"]["examsToSymptoms"]["data"]);
                strs.push(...keys);
                break;
            case SpecialPageTypes.Treatments:
                keys = Object.keys(HospitalIDMapper["mapMap"]["treatmentsToSymptoms"]["data"]);
                strs.push(...keys);
                break;
        }
        strs = strs.map((e, i) => {
            if(!HospitalIDMapper.idToStringObject[e]) {
                // console.error(e);
                return null;
            }
            return {
                id: e,
                name: HospitalIDMapper.idToStringObject[e],
                type: _type,
                // todo: add department and stuff
                // department: asdf,
            };
        }).filter((e) => Boolean(e));
        return strs;
    }

    onStep(_dt: number) {
        super.onStep(_dt);
        if(!this.visible || !this.parent || !this.acceptingInput) return;
        this._caratTick += _dt * 20;
        if(this._caratTick >= 1000) {
            this._caratTick = 0;
            this._carat.visible = !this._carat.visible;
        }
    }
}